import React from "react";
import { nanoid } from "nanoid";
import { useTranslation } from "react-i18next";
import {
  Stack,
  Typography,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  TextField,
  IconButton,
} from "@mui/material";
import SubActionButton from "@quest_editor/components/Buttons/SubActionButton";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ClearIcon from "@mui/icons-material/Clear";
import { Store, stores } from "@quest_editor/mocks";

const TargetStoreField = ({
  targetStoreRows,
  setTargetStoreRows,
  emptyLabel,
}: {
  targetStoreRows: Store[];
  setTargetStoreRows: React.Dispatch<React.SetStateAction<Store[]>>;
  emptyLabel: string;
}) => {
  const { t } = useTranslation("questEditor");
  const [phoneNumbers, setPhoneNumbers] = React.useState<string>("");

  const handleRemoveTargetStore = (id: string) => {
    setTargetStoreRows((prev: Store[]) => prev.filter((row) => row.id !== id));
  };

  const handleAddTargetStore = () => {
    if (!phoneNumbers.trim()) return;

    const phoneNumberList = phoneNumbers
      .split(/[,\r\n]+/)
      .map((p) => p.trim())
      .filter(Boolean);

    const foundStores: Store[] = phoneNumberList
      .map((phoneNumber) => {
        const found = stores.find((s) => s.phoneNumber === phoneNumber);
        if (found) {
          return { ...found, id: nanoid() };
        }
        return null;
      })
      .filter((s: Store | null): s is Store => s !== null);

    setTargetStoreRows((prev: Store[]) => {
      const existingPhoneNumbers = new Set(prev.map((s) => s.phoneNumber));
      const uniqueNew = foundStores.filter(
        (s) => !existingPhoneNumbers.has(s.phoneNumber),
      );
      return [...prev, ...uniqueNew];
    });
    setPhoneNumbers("");
  };

  return (
    <Accordion
      sx={{
        ".MuiAccordionSummary-root .MuiSvgIcon-root": {
          transform: "rotate(0deg)",
        },
      }}
    >
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        {targetStoreRows.length > 0 ? (
          <Typography color="primary">
            {targetStoreRows.map((store) => store.name).join(", ")}
          </Typography>
        ) : (
          <Typography color="primary">{emptyLabel}</Typography>
        )}
      </AccordionSummary>
      <AccordionDetails>
        <Stack direction="column" divider={<Divider />} spacing={1}>
          {targetStoreRows.map((row: Store) => (
            <Stack
              key={row.id}
              direction="row"
              alignItems="center"
              spacing={4}
            >
              <Typography sx={{ color: "text.secondary", flexGrow: 1 }}>
                {row.name}
              </Typography>
              <Typography sx={{ color: "text.secondary", flexGrow: 1 }}>
                {row.phoneNumber}
              </Typography>
              <Typography sx={{ color: "text.secondary", flexGrow: 1 }}>
                {t("common_field.target_store.store_count", {
                  count: row.storeCount,
                })}
              </Typography>
              <IconButton onClick={() => handleRemoveTargetStore(row.id)}>
                <ClearIcon />
              </IconButton>
            </Stack>
          ))}
          <Stack direction="row" alignItems="center" spacing={1}>
            <TextField
              size="small"
              placeholder={t("common_field.target_store.placeholder")}
              value={phoneNumbers}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setPhoneNumbers(e.target.value)
              }
              multiline
              rows={4}
              sx={{ flexGrow: 1 }}
            />
            <SubActionButton
              onClick={handleAddTargetStore}
              sx={{ whiteSpace: "nowrap" }}
            >
              {t("common_field.target_store.button")}
            </SubActionButton>
          </Stack>
        </Stack>
      </AccordionDetails>
    </Accordion>
  );
};

export default TargetStoreField;
